import React, { useEffect, useMemo, useState } from "react";
import {
    DndContext,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import ServiceRow from "./ServiceRow";
import ServiceEditor from "./ServiceEditor";
import {
    fetchServices,
    upsertService,
    reorderServices,
    deleteService,
    seedServices,
} from "../../services/servicesService";
import { isSupabaseConfigured } from "../../lib/supabaseClient";

export default function ServiceManager() {
    const [services, setServices] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reorderMessage, setReorderMessage] = useState("");
    const [formNotice, setFormNotice] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [hasDraftChanges, setHasDraftChanges] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [supabaseNotice, setSupabaseNotice] = useState("");
    const [isSeeding, setIsSeeding] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor)
    );

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const data = await fetchServices();
                const sorted = [...data].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
                setServices(sorted);
                setSelectedId((prev) => prev ?? sorted[0]?.id ?? null);
                if (isSupabaseConfigured) {
                    setSupabaseNotice(
                        sorted.length
                            ? "Supabase bağlantısı aktif."
                            : "Supabase tablosu boş. Demo hizmet setini yüklemek için butonu kullanın."
                    );
                } else {
                    setSupabaseNotice("Supabase anahtarları tanımlı değil; değişiklikler bu oturumda kalır.");
                }
            } catch (fetchError) {
                console.error(fetchError);
                setError("Hizmetler yüklenemedi.");
                if (isSupabaseConfigured) {
                    setSupabaseNotice("Supabase bağlantısında sorun oluştu. Ayarlarınızı kontrol edin.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        if (!selectedId && services.length) {
            setSelectedId(services[0].id);
        }
    }, [services, selectedId]);

    useEffect(() => {
        if (!selectedId) {
            setDraft(null);
            return;
        }
        const original = services.find((item) => item.id === selectedId);
        if (original) {
            setDraft(cloneService(original));
            setHasDraftChanges(false);
        }
    }, [services, selectedId]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        setServices((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            const reordered = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
                ...item,
                orderIndex: index,
            }));
            queueMicrotask(() => persistReorder(reordered));
            return reordered;
        });
    };

    const persistReorder = async (nextOrder) => {
        if (!isSupabaseConfigured) {
            setReorderMessage("Sıralama Supabase bağlı olduğunda kaydedilir. Şimdilik sadece önizleme değişti.");
            return;
        }

        try {
            setReorderMessage("Sıralama kaydediliyor...");
            await reorderServices(nextOrder.map((item) => item.id));
            setReorderMessage("Sıralama güncellendi.");
        } catch (persistError) {
            console.error(persistError);
            setReorderMessage("Sıralama kaydedilemedi. Tekrar deneyin.");
        }
    };

    const handleSeedSupabase = async () => {
        if (!isSupabaseConfigured) {
            setSupabaseNotice("Supabase yapılandırması bulunamadı.");
            return;
        }

        setIsSeeding(true);
        setSupabaseNotice("Demo hizmet seti Supabase'e aktarılıyor...");
        try {
            const seeded = await seedServices();
            setServices(seeded);
            setSelectedId((prev) => prev ?? seeded[0]?.id ?? null);
            setSupabaseNotice("Supabase hizmet tablosu güncellendi.");
        } catch (seedError) {
            console.error(seedError);
            setSupabaseNotice("Supabase'e veri aktarılırken hata oluştu.");
        } finally {
            setIsSeeding(false);
        }
    };

    const infoMessage = useMemo(() => {
        if (isLoading) {
            return "Hizmet içerikleri yükleniyor...";
        }
        if (error) {
            return error;
        }
        if (!services.length) {
            return "Henüz hizmet kaydı eklenmemiş.";
        }
        return "";
    }, [isLoading, error, services]);

    const filteredServices = useMemo(() => {
        if (!searchTerm.trim()) {
            return services;
        }
        const query = searchTerm.trim().toLowerCase();
        return services.filter((item) =>
            [
                item.title,
                item.cardTitle,
                item.cardSummary,
                item.intro,
                item.category,
                ...(item.focusPoints ?? []),
                ...(item.highlights ?? []),
            ]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(query))
        );
    }, [services, searchTerm]);

    const handleSelect = (id) => {
        setSelectedId(id);
        setFormNotice("");
    };

    const handleDraftChange = (nextDraft) => {
        setDraft(nextDraft);
        setHasDraftChanges(true);
        setFormNotice("");
    };

    const handleResetDraft = () => {
        if (!selectedId) {
            return;
        }
        const original = services.find((item) => item.id === selectedId);
        if (original) {
            setDraft(cloneService(original));
            setHasDraftChanges(false);
            setFormNotice("Değişiklikler sıfırlandı.");
        }
    };

    const handleCreate = () => {
        const newEntry = createBlankService(services.length);
        setServices((items) => {
            const next = [newEntry, ...items].map((item, index) => ({ ...item, orderIndex: index }));
            return next;
        });
        setSelectedId(newEntry.id);
        setDraft(newEntry);
        setHasDraftChanges(true);
        setFormNotice("Yeni hizmet taslağı oluşturuldu.");
    };

    const commitToState = (savedService) => {
        setServices((items) => {
            const exists = items.some((item) => item.id === savedService.id);
            if (exists) {
                return items.map((item) => (item.id === savedService.id ? savedService : item));
            }
            const next = [savedService, ...items];
            return next.map((item, index) => ({ ...item, orderIndex: index }));
        });
        setDraft(cloneService(savedService));
        setSelectedId(savedService.id);
        setHasDraftChanges(false);
    };

    const handleSave = async () => {
        if (!draft) {
            return;
        }
        setIsSaving(true);
        setFormNotice("");
        const sanitized = sanitizeService(draft);

        try {
            if (!isSupabaseConfigured) {
                commitToState(sanitized);
                setFormNotice("Supabase tanımlı olmadığı için değişiklikler bu oturumda tutuluyor.");
                return;
            }

            const saved = await upsertService(sanitized);
            commitToState(saved);
            setFormNotice("Hizmet kaydı kaydedildi.");
        } catch (persistError) {
            console.error(persistError);
            setFormNotice("Kaydetme sırasında hata oluştu.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!id) {
            return;
        }

        try {
            if (isSupabaseConfigured) {
                await deleteService(id);
            }
            setServices((items) => items.filter((item) => item.id !== id));
            setFormNotice(isSupabaseConfigured ? "Hizmet kaydı silindi." : "Taslak silindi (Supabase bağlı değil).");

            if (selectedId === id) {
                const fallback = services.find((item) => item.id !== id);
                setSelectedId(fallback?.id ?? null);
                setDraft(fallback ? cloneService(fallback) : null);
                setHasDraftChanges(false);
            }
        } catch (deleteError) {
            console.error(deleteError);
            setFormNotice("Silme sırasında hata oluştu.");
        }
    };

    const handleDuplicate = () => {
        if (!draft) {
            return;
        }
        const duplicated = cloneService(draft);
        duplicated.id = generateId();
        duplicated.slug = `${duplicated.slug || "hizmet"}-${Math.random().toString(36).slice(2, 6)}`;
        duplicated.title = `${duplicated.title || "Yeni hizmet"} (Kopya)`;
        duplicated.published = false;
        duplicated.orderIndex = 0;

        setServices((items) => {
            const next = [duplicated, ...items].map((item, index) => ({ ...item, orderIndex: index }));
            return next;
        });
        setSelectedId(duplicated.id);
        setDraft(duplicated);
        setHasDraftChanges(true);
        setFormNotice("Taslak kopyalandı. Kaydetmek için değişiklikleri kaydet butonuna basın.");
    };

    const handleTogglePublished = async (id) => {
        const target = services.find((item) => item.id === id);
        if (!target) {
            return;
        }
        const nextState = { ...target, published: !target.published };

        if (!isSupabaseConfigured) {
            commitToState(nextState);
            setFormNotice("Supabase bağlı olmadığı için yayın durumu bu oturumda değiştirildi.");
            return;
        }

        setIsSaving(true);
        try {
            const saved = await upsertService(nextState);
            commitToState(saved);
            setFormNotice(saved.published ? "Hizmet yayına alındı." : "Hizmet taslak yapıldı.");
        } catch (toggleError) {
            console.error(toggleError);
            setFormNotice("Yayın durumu güncellenemedi.");
        } finally {
            setIsSaving(false);
        }
    };

    const isFiltering = Boolean(searchTerm.trim());
    const listForRender = filteredServices;

    return (
        <section className="package-manager" aria-label="Hizmetler modülü">
            <header className="package-manager__toolbar">
                <div>
                    <h2 className="admin-shell__title" style={{ fontSize: "1.4rem" }}>Hizmetler</h2>
                    <p className="admin-shell__subtitle" style={{ maxWidth: "760px" }}>
                        Tüm hizmet kapsamlarını, teslim paketlerini ve SSS içeriklerini burada yönetin. Supabase etkinleştirildiğinde değişiklikler otomatik kaydedilir.
                    </p>
                </div>
                <div className="package-manager__actions">
                    {isSupabaseConfigured ? (
                        <button
                            type="button"
                            className="signature-manager__button signature-manager__button--ghost"
                            onClick={handleSeedSupabase}
                            disabled={isSeeding}
                        >
                            {isSeeding ? "Supabase senkronize ediliyor..." : "Supabase verisini senkronize et"}
                        </button>
                    ) : (
                        <span className="signature-manager__status">Supabase pasif</span>
                    )}
                    <div className="package-manager__search">
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Hizmetlerde ara..."
                        />
                    </div>
                    <button type="button" className="signature-manager__button" onClick={handleCreate}>
                        Yeni hizmet
                    </button>
                </div>
            </header>

            {infoMessage && <p style={{ color: "rgba(224, 229, 245, 0.68)" }}>{infoMessage}</p>}

            {!!reorderMessage && (
                <p style={{ color: "rgba(200, 167, 232, 0.75)", fontSize: "0.85rem", letterSpacing: "0.18rem" }}>{reorderMessage}</p>
            )}

            {!!supabaseNotice && (
                <p className="signature-manager__flash" style={{ borderColor: "rgba(200, 167, 232, 0.35)" }}>{supabaseNotice}</p>
            )}

            <div className="package-manager__workspace">
                <div className="package-manager__catalog" aria-label="Hizmet listesi">
                    {isFiltering && !listForRender.length ? (
                        <div className="signature-manager__empty">Arama sonucunda hizmet bulunamadı.</div>
                    ) : null}

                    {isFiltering ? (
                        <div className="package-manager__list">
                            {listForRender.map((item) => (
                                <ServiceRow
                                    key={item.id}
                                    item={item}
                                    isActive={item.id === selectedId}
                                    onSelect={handleSelect}
                                    onTogglePublished={handleTogglePublished}
                                    sortableDisabled
                                />
                            ))}
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
                            <SortableContext items={services.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                <div className="package-manager__list">
                                    {services.map((item) => (
                                        <ServiceRow
                                            key={item.id}
                                            item={item}
                                            isActive={item.id === selectedId}
                                            onSelect={handleSelect}
                                            onTogglePublished={handleTogglePublished}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                <ServiceEditor
                    draft={draft}
                    onChange={handleDraftChange}
                    onSave={handleSave}
                    onReset={handleResetDraft}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    saving={isSaving}
                    hasChanges={hasDraftChanges}
                    supabaseEnabled={isSupabaseConfigured}
                />
            </div>

            {!!formNotice && <p className="signature-manager__flash">{formNotice}</p>}
        </section>
    );
}

const cloneService = (payload) => JSON.parse(JSON.stringify(payload));

const generateId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `service-${Math.random().toString(36).slice(2, 10)}`;
};

const createBlankService = (orderIndex) => ({
    id: generateId(),
    slug: "",
    category: "drone",
    cardTitle: "YENİ HİZMET",
    cardSummary: "Servis kartı özet metni",
    title: "",
    intro: "",
    focusPoints: [""],
    highlights: [""],
    deliverables: [""],
    process: [""],
    tools: [""],
    faq: [{ question: "", answer: "" }],
    ctaLabel: "Brief iste",
    ctaUrl: "mailto:studio@modelerman.com",
    orderIndex,
    published: false,
});

const sanitizeService = (payload) => {
    const next = cloneService(payload);
    next.slug = next.slug?.trim() || next.id;
    next.category = next.category?.trim() || "general";
    next.cardTitle = next.cardTitle?.trim() || next.title?.toUpperCase() || "HİZMET";
    next.cardSummary = next.cardSummary?.trim() || "";
    next.title = next.title?.trim() || next.cardTitle;
    next.intro = next.intro?.trim() || "";
    next.ctaLabel = next.ctaLabel?.trim() || "Brief iste";
    next.ctaUrl = next.ctaUrl?.trim() || "mailto:studio@modelerman.com";

    next.focusPoints = (next.focusPoints ?? []).map((entry) => entry.trim()).filter(Boolean);
    if (!next.focusPoints.length) {
        next.focusPoints = [];
    }
    next.highlights = (next.highlights ?? []).map((entry) => entry.trim()).filter(Boolean);
    next.deliverables = (next.deliverables ?? []).map((entry) => entry.trim()).filter(Boolean);
    next.process = (next.process ?? []).map((entry) => entry.trim()).filter(Boolean);
    next.tools = (next.tools ?? []).map((entry) => entry.trim()).filter(Boolean);
    next.faq = (next.faq ?? [])
        .map((entry) => ({
            question: entry.question?.trim() || "",
            answer: entry.answer?.trim() || "",
        }))
        .filter((entry) => entry.question || entry.answer);

    if (!next.faq.length) {
        next.faq = [];
    }

    return next;
};
